import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useFormik } from "formik";
import { ReactComponent as InfoIcon1 } from "../../../Assets/Icons/info.svg";
import { ReactComponent as WhitePlainTick } from "../../../Assets/Icons/whiteplaintick.svg";
import { Tooltip } from "antd";
import { ReactComponent as BlueCrossIcon } from "../../../Assets/Icons/blueCross.svg";
import { rulesOfferSchema } from "../../../utils/validationSchemas";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import {
  createOffer,
  createRule,
  deleteRule,
  getRules,
} from "../../../services/offerService";
import declinedIcon from "../../../Assets/Icons/declinedIcon.svg";
import pendingIcon from "../../../Assets/Icons/pendingIcon.svg";
import { useOfferFormContext } from "./OfferFormContext";
import "./offerform.scss";
import { scrollToFirstError } from "../../../utils/utilFunctions";

// Move sub-components out to prevent re-mounting issues
const RuleOption = ({ rule, isChecked, onCheck, statusIcon }) => {
  return (
    <div
      className={`rule-item-container ${isChecked ? 'selected' : ''}`}
    >
      <div className="rule-checkbox" onClick={() => onCheck(rule)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <span className="rule-label">{rule.title}</span>
      {statusIcon && <img src={statusIcon} className="w-4 h-4 ml-1 mt-1" alt="" />}
    </div>
  );
};

const CustomRuleBox = ({ rule, isChecked, isEditing, editValue, setEditValue, onCheck, onEdit, onCancel, onSave, onDelete, statusIcon }) => {
  if (isEditing) {
    return (
      <div className="custom-rule-item">
        <div className="edit-input-wrapper">
          <input
            autoFocus
            placeholder="Enter rules"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave(rule);
              if (e.key === "Escape") onCancel();
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="edit-btn-group">
            <span
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              className="text-[#767676] text-[12px] font-[400] font-poppins cursor-pointer hover:underline mr-1"
            >
              Cancel
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); onSave(rule); }}
              className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer select-none"
            >
              Save
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`custom-rule-item ${isChecked ? 'selected' : ''}`}>
      <div className="left-section">
        <div className={`checkbox-mini ${isChecked ? 'checked' : ''}`} onClick={() => onCheck(rule)}>
          {isChecked && <WhitePlainTick className="checkmark-pop" />}
        </div>
        <span className="rule-label">{rule.title}</span>
        {statusIcon && <img src={statusIcon} className="w-4 h-4" alt="" />}
      </div>
      <div className="actions" onClick={(e) => e.stopPropagation()}>
        <span onClick={() => onEdit(rule)}>Edit</span>
        <BlueCrossIcon onClick={() => onDelete(rule)} className="delete-x-icon" />
      </div>
    </div>
  );
};

const RulesRegulations = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  handleSaveAndExit
}) => {
  const { registerNextAction, setIsNextLoading, setIsNextDisabled } = useOfferFormContext();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const selectedBusiness = useSelector((state) => state?.merchantReducer?.selectedMerchent);

  const [addClicked, setAddClicked] = useState(false);
  const [allRules, setAllRules] = useState([]);
  const [predefinedRules, setPredefinedRules] = useState([]);
  const [customApprovedRules, setCustomApprovedRules] = useState([]);

  // Local interaction state
  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedRulesIds, setSelectedRulesIds] = useState([]);
  const [offerPendingRule, setOfferPendingRule] = useState([]);

  const [newRule, setNewRule] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deletedRules, setDeletedRules] = useState([]);
  const [hiddenCustomIds, setHiddenCustomIds] = useState([]);
  const [hasError, setHasError] = useState(false);

  // CRITICAL: Prevent re-initialization once the user starts interacting
  const hasInitializedSync = useRef(false);

  const currentOfferType = offerData?.offerType || editOfferData?.offerType;

  const merchantIdForQuery = useMemo(() => {
    const adminSelected = (role === "admin" || role === "ADMIN") && selectedMerchant?._id;
    if (adminSelected) return selectedMerchant?._id;

    return selectedMerchant?._id ||
      offerData?.merchant?._id ||
      offerData?.merchantId ||
      editOfferData?.merchant?._id ||
      editOfferData?.merchantId;
  }, [role, selectedMerchant?._id, offerData?.merchant?._id, offerData?.merchantId, editOfferData?.merchant?._id, editOfferData?.merchantId]);

  // Handle store/merchant sync for admin
  useEffect(() => {
    if (role === "admin") {
      const biz =
        selectedMerchant ||
        offerData?.merchant ||
        editOfferData?.merchant ||
        (offerData?.merchantId ? { _id: offerData.merchantId } : null) ||
        (editOfferData?.merchantId ? { _id: editOfferData.merchantId } : null);
      if (biz && (!selectedBusiness || selectedBusiness?._id !== biz?._id)) {
        if (biz._id && (biz.businessName || biz.name)) {
          dispatch(setCurrentBusiness(biz));
        }
      }
    }
  }, [role, selectedMerchant, offerData?.merchant, offerData?.merchantId, editOfferData?.merchant, editOfferData?.merchantId, selectedBusiness, dispatch]);

  const getRulesData = useCallback(async () => {
    if (!currentOfferType) return;
    try {
      const [generalRes, customRes] = await Promise.all([
        dispatch(getRules({ offerType: currentOfferType }))?.unwrap(),
        dispatch(
          getRules(
            merchantIdForQuery
              ? { offerType: currentOfferType, merchantId: merchantIdForQuery, merchantid: merchantIdForQuery }
              : { offerType: currentOfferType }
          )
        )?.unwrap(),
      ]);

      const generalApproved = Array.isArray(generalRes?.data) ? generalRes.data.filter((r) => r?.title && r?.isCustom === false) : [];
      const customCandidates = Array.isArray(customRes?.data) ? customRes.data.filter((r) => r?.title) : [];
      const explicitCustom = customCandidates.filter((r) => r?.merchantId || r?.merchantid || r?.merchant?._id || r?.isCustom === true);
      const generalTitles = new Set(generalApproved.map((r) => r.title));
      const derivedCustom = customCandidates.filter((r) => !generalTitles.has(r.title));
      const finalCustom = explicitCustom.length > 0 ? explicitCustom : derivedCustom;

      const uniqueByTitle = (arr) => {
        const seen = new Set();
        const out = [];
        for (const r of arr) {
          const key = String(r?.title || "").trim().toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          out.push(r);
        }
        return out;
      };

      const uniqueGeneral = uniqueByTitle(generalApproved);
      const uniqueCustom = uniqueByTitle(finalCustom);

      setPredefinedRules(uniqueGeneral);
      setCustomApprovedRules(uniqueCustom);
      setAllRules([...(uniqueGeneral || []), ...(uniqueCustom || [])]);
      setHasError(false); // Clear error on success
    } catch (error) {
      console.error("Error fetching rules:", error);
      setHasError(true); // Set error state
    }
  }, [currentOfferType, merchantIdForQuery, dispatch]);

  useEffect(() => {
    if (currentOfferType) getRulesData();
  }, [currentOfferType, merchantIdForQuery, getRulesData]);

  // INITIALIZATION: Run once when rules are available from API
  useEffect(() => {
    if (hasInitializedSync.current) return;
    if (allRules.length > 0) {
      const sourceData = offerData?._id ? offerData : editOfferData;
      const ruleObjects = [];
      const ruleIds = [];
      const pendingSource = [];

      if (sourceData?.ruleAndRegulation) {
        sourceData.ruleAndRegulation.forEach((rule) => {
          const id = typeof rule === "string" ? rule : rule?._id;
          const matched = allRules.find(r => r._id === id) || (typeof rule === 'object' ? rule : null);
          if (matched) {
            ruleObjects.push(matched);
            ruleIds.push(matched._id);
            if (matched.status === "pending" || matched.status === "rejected") {
              pendingSource.push(matched);
            }
          }
        });
      }

      setSelectedRules(ruleObjects);
      setSelectedRulesIds(ruleIds);
      setOfferPendingRule(pendingSource);
      hasInitializedSync.current = true;
    }
  }, [allRules, offerData, editOfferData]);

  const initialValues = { ruleAndRegulation: [] };
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: rulesOfferSchema,
    onSubmit: async (values) => {
      const createPromise = await dispatch(createOffer({
        ruleAndRegulation: selectedRulesIds,
        offerId: isEdit ? editOfferData?._id : offerData?._id,
        merchantId: selectedMerchant?._id,
      }))?.unwrap();
      if (createPromise?.success === true) {
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const { setFieldValue, setFieldTouched, handleSubmit, errors, touched } = formik;

  useEffect(() => {
    const titles = selectedRules.map(r => r.title).filter(Boolean);
    setFieldValue("ruleAndRegulation", titles, false);
  }, [selectedRules, setFieldValue]);

  useEffect(() => {
    setPayloadData({
      ruleAndRegulation: selectedRulesIds,
      id: offerData?._id,
      merchantId: merchantIdForQuery || selectedMerchant?._id || null,
      isCreated: false,
    });
  }, [selectedRulesIds, offerData?._id, merchantIdForQuery, selectedMerchant?._id, setPayloadData]);

  const attemptNext = async () => {
    formik.setTouched({ ruleAndRegulation: true });
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      scrollToFirstError(validationErrors, ["ruleAndRegulation"]);
      return;
    }
    formik.handleSubmit();
  };

  useEffect(() => { registerNextAction(() => attemptNext()); }, [registerNextAction, selectedRulesIds]);
  useEffect(() => { setIsNextLoading(loading); }, [loading, setIsNextLoading]);

  // Update disabled state for Next button
  useEffect(() => {
    setIsNextDisabled(selectedRulesIds.length === 0);
  }, [selectedRulesIds, setIsNextDisabled]);

  // Interaction handlers
  const handleRuleChecked = useCallback((rule) => {
    const targetId = rule._id;
    setSelectedRulesIds(prev => {
      const isChecked = prev.includes(targetId);
      if (isChecked) {
        setSelectedRules(curr => curr.filter(r => r._id !== targetId));
        return prev.filter(id => id !== targetId);
      } else {
        setSelectedRules(curr => [...curr, rule]);
        return [...prev, targetId];
      }
    });
  }, []);

  const handleEditClick = (rule) => {
    if (!rule) return;
    setEditingId(rule._id);
    setEditValue(rule.title);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleSaveEdit = (rule) => {
    if (!editValue.trim()) return;
    const update = (list) => list.map(r => r._id === rule._id ? { ...r, title: editValue } : r);
    setAllRules(prev => update(prev));
    setPredefinedRules(prev => update(prev));
    setCustomApprovedRules(prev => update(prev));
    setOfferPendingRule(prev => update(prev));
    setSelectedRules(prev => update(prev));
    setEditingId(null);
  };

  const handleDeleteClick = (rule) => {
    // Dispatch DELETE API call
    dispatch(deleteRule({ id: rule._id, merchantId: merchantIdForQuery }));

    const filter = (list) => list.filter(r => r._id !== rule._id);
    setAllRules(prev => filter(prev));
    setPredefinedRules(prev => filter(prev));
    setCustomApprovedRules(prev => filter(prev));
    setOfferPendingRule(prev => filter(prev));
    setSelectedRules(prev => filter(prev));
    setSelectedRulesIds(prev => prev.filter(id => id !== rule._id));

    // Track as hidden/deleted for duplicate check logic to prevent 404s on re-delete
    setHiddenCustomIds(prev => [...prev, rule._id]);

    // Store in deleted list - strictly for reference, creation logic will prefer new API call
    setDeletedRules(prev => [...prev, rule]);
  };

  const handleNewRuleSubmit = async () => {
    const title = (newRule || "").trim();
    if (title === "") return;

    const normToCreate = title.toLowerCase();

    // 1. Check if it's in the 'deleted' list
    const deletedMatch = deletedRules.find(r => r.title.toLowerCase() === normToCreate);
    if (deletedMatch) {
      // It was deleted (and removed from server). Clean up local reference and create FRESH.
      setDeletedRules(prev => prev.filter(r => r._id !== deletedMatch._id));
    }

    // 2. Check if it's already in the main lists but just not selected
    // Ignore items that are currently hidden/deleted
    const existingMatch = allRules
      .filter(r => !hiddenCustomIds.includes(r._id))
      .find(r => r.title.toLowerCase() === normToCreate);

    if (existingMatch) {
      if (!selectedRulesIds.includes(existingMatch._id)) {
        setSelectedRules(prev => [...prev, existingMatch]);
        setSelectedRulesIds(prev => [...prev, existingMatch._id]);
      }
      setNewRule("");
      setAddClicked(false);
      return;
    }

    const res = await dispatch(createRule({ title, offerId: offerData?._id, offerType: currentOfferType, merchantId: merchantIdForQuery }))?.unwrap();
    if (res?.success === true) {
      const data = res?.data;

      const cleanUpdate = (prevList) => {
        const list = Array.isArray(prevList) ? prevList : [];
        // Remove any ghost items (same name + hidden) to prevent duplicates
        return [
          ...list.filter(r => !(hiddenCustomIds.includes(r._id) && r.title.toLowerCase() === title.toLowerCase())),
          data
        ];
      };

      setAllRules(cleanUpdate);
      if (role === "admin") setCustomApprovedRules(cleanUpdate);
      else setOfferPendingRule(cleanUpdate);

      setSelectedRules(prev => [...prev, data]);
      setSelectedRulesIds(prev => [...prev, data._id]);
      setNewRule("");
      setAddClicked(false);
    }
  };

  const splitArray = (arr) => {
    const col1 = [];
    const col2 = [];
    arr.forEach((item, i) => {
      if (i % 2 === 0) col1.push(item);
      else col2.push(item);
    });
    return [col1, col2];
  };

  const [preCol1, preCol2] = splitArray(predefinedRules);
  const allCustomItems = useMemo(() => {
    const items = [...(customApprovedRules || []), ...(offerPendingRule || [])];
    const seen = new Set();
    const out = [];
    items.forEach(item => {
      if (item?._id && !seen.has(item._id)) {
        seen.add(item._id);
        out.push(item);
      }
    });
    return out;
  }, [customApprovedRules, offerPendingRule]);

  return (
    <div className="w-full flex flex-col items-center offer-rules-step">
      <div className="w-full max-w-[1100px] relative mt-20">
        <div className="absolute top-[-75px] right-0">
          <button onClick={handleSaveAndExit} className="px-6 h-[40px] border border-[#00ADEF] rounded-[5px] text-[#00ADEF] text-[14px] font-[500] font-poppins hover:bg-[#F0FBFF] transition-all">
            Save and Exit
          </button>
        </div>

        <div className="w-full max-w-[850px] mx-auto mb-6" data-field="ruleAndRegulation">
          <div className="w-full mb-6 relative">
            <h2 className="text-[32px] font-[600] text-[#303030] font-poppins">
              Set Your Offer <span className="text-[#00ADEF]">Rules & Regulations</span>
            </h2>
            <p className="text-[#767676] text-[14px] font-[400] font-poppins mt-1">
              Let customers know how and when they can use your offer.
            </p>
            {selectedRulesIds.length > 0 && (
              <div className="rules-header-right">
                {selectedRulesIds.length} Selected
              </div>
            )}
          </div>

          {!currentOfferType ? (
            <div className="bg-[#F6FBFE] border border-[#CDEFFB] text-[#086788] rounded-[10px] p-6 text-sm font-poppins flex items-center gap-3">
              <InfoIcon1 className="w-5 h-5 flex-shrink-0" />
              Please select an offer type in the "Create Offer" step to view the suggested Rules & Regulations.
            </div>
          ) : hasError ? (
            <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[#FFD6D6] rounded-[10px] bg-[#FFF5F5]">
              <p className="text-[#D32F2F] font-poppins text-[14px] font-[500]">
                Something Went Wrong
              </p>
              <p className="text-[#B0B0B0] font-poppins text-[12px] mt-2">
                Unable to load rules. Please try again.
              </p>
            </div>
          ) : allRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[#D9D9D9] rounded-[10px] bg-[#FAFAFA]">
              <p className="text-[#767676] font-poppins text-[14px]">
                No Rules & Regulations Found
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                <div className="flex flex-col">
                  {preCol1.map(r => <RuleOption key={r._id} rule={r} isChecked={selectedRulesIds.includes(r._id)} onCheck={handleRuleChecked} />)}
                </div>
                <div className="flex flex-col">
                  {preCol2.map(r => <RuleOption key={r._id} rule={r} isChecked={selectedRulesIds.includes(r._id)} onCheck={handleRuleChecked} />)}
                </div>
              </div>

              <div className="rules-separator">
                <div className="line"></div><span>Or</span><div className="line"></div>
              </div>

              {(allCustomItems.length > 0 || addClicked) && (
                <div className="custom-rules-container mb-6">
                  <label className="custom-rules-label">Custom Rules & Regulations</label>
                  <div className="custom-rules-grid">
                    {allCustomItems.map(rule => (
                      <CustomRuleBox
                        key={rule._id}
                        rule={rule}
                        isChecked={selectedRulesIds.includes(rule._id)}
                        isEditing={editingId === rule._id}
                        editValue={editValue}
                        setEditValue={setEditValue}
                        onCheck={handleRuleChecked}
                        onEdit={handleEditClick}
                        onCancel={handleCancelEdit}
                        onSave={handleSaveEdit}
                        onDelete={handleDeleteClick}
                        statusIcon={rule.status === 'pending' ? pendingIcon : (rule.status === 'rejected' ? declinedIcon : null)}
                      />
                    ))}

                    {addClicked && (
                      <div className="col-span-1 w-full rule-add-input-area animate-fadeInScale m-0" style={{ marginBottom: 0 }}>
                        <div className="input-row">
                          <input
                            type="text"
                            placeholder="Enter rules"
                            className="w-full h-[45px] bg-white border border-[#D9D9D9] rounded-[5px] px-3 outline-none text-[13px] font-poppins text-[#303030] focus:border-[#00ADEF] transition-colors"
                            name="newRule"
                            value={newRule}
                            autoFocus
                            onChange={(e) => setNewRule(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleNewRuleSubmit();
                              }
                            }}
                          />
                          {(() => {
                            const normNew = (newRule || "").trim().toLowerCase();
                            if (!normNew) return null;
                            const exists = allRules
                              .filter(r => !hiddenCustomIds.includes(r._id))
                              .some(r => r.title.toLowerCase() === normNew);
                            if (exists) {
                              return (
                                <div className="mt-1 ms-1 text-start">
                                  <span className="text-red-600 text-[10px] md:text-[12px] font-[600]">
                                    This rule already exists.
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="btn-row justify-start">
                          <span
                            className="cancel-link"
                            onClick={() => { setAddClicked(false); setNewRule(""); }}
                          >
                            Cancel
                          </span>
                          <button
                            type="button"
                            className="add-confirm-btn"
                            disabled={!newRule.trim()}
                            onClick={handleNewRuleSubmit}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4">
                <button
                  className={`add-rule-btn-large ${addClicked ? 'is-disabled' : ''}`}
                  onClick={() => !addClicked && setAddClicked(true)}
                >
                  <div className={`plus-circle flex items-center justify-center p-0 ${addClicked ? '!bg-[#8C8C8C]' : 'bg-white'}`}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="m-0 p-0">
                      <path
                        d="M6 1V11M1 6H11"
                        stroke={addClicked ? "#E0E0E0" : "#00ADEF"}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  Add another type of Rules & Regulations
                </button>
                <Tooltip
                  title="Add your own rule that isn't listed above."
                  color="#00ADEF"
                  placement={window.innerWidth < 640 ? "top" : "right"}
                  overlayInnerStyle={window.innerWidth < 640 ?
                    { maxWidth: '280px', fontSize: '12px', padding: '8px 12px', textAlign: 'center' } :
                    { maxWidth: '350px', fontSize: '14px', padding: '10px 15px', fontWeight: '500', lineHeight: '1.4' }
                  }
                >
                  <span className="flex items-center cursor-pointer">
                    <svg width="20" height="20" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M9.00033 5.56675H9.00619M9.00033 11.4334V7.76675M16.3337 8.50008C16.3337 12.5503 13.0505 15.8334 9.00033 15.8334C4.95013 15.8334 1.66699 12.5503 1.66699 8.50008C1.66699 4.44988 4.95013 1.16675 9.00033 1.16675C13.0505 1.16675 16.3337 4.44988 16.3337 8.50008Z"
                        stroke={addClicked ? "#BFBFBF" : "#00ADEF"}
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Tooltip>
              </div>
            </>
          )}

          {
            errors.ruleAndRegulation && touched.ruleAndRegulation && (
              <div className="text-red-600 text-[11px] font-poppins mt-6">{errors.ruleAndRegulation}</div>
            )
          }
        </div >
      </div >
    </div >
  );
};

export default RulesRegulations;
